import { CSSProperties, PropsWithChildren, forwardRef } from 'react';

import { Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps } from '@mui/material';

export type TooltipProps = MuiTooltipProps & {
  variant?: 'light' | 'dark';
  display?: CSSProperties['display'];
  /**
   * Hand the child straight to MUI instead of wrapping it in the ref-safety `<span>`. Only for
   * children that already forward their ref and spread their props — a `styled()` DOM element does.
   *
   * Needed whenever the child itself must receive MUI's ARIA wiring: `describeChild` clones
   * `aria-describedby` onto MUI's *direct* child, so with the wrapper in place the description binds
   * to the span and the real control never advertises it. Hover and focus still open the tooltip, so
   * the omission is invisible until someone uses a screen reader.
   *
   * Makes `display` a no-op: there is no span left to lay out.
   */
  disableWrapper?: boolean;
};

/**
 * Handle MUI forwardRef issue https://mui.com/material-ui/guides/composition/#caveat-with-refs
 *
 * The tooltip needs to apply DOM event listeners to its child element.
 * If the child is a custom React element, you need to make sure that it spreads its props to the underlying DOM element.
 */
const TooltipContent = forwardRef<HTMLSpanElement, PropsWithChildren<Pick<TooltipProps, 'display'>>>(
  ({ display, ...props }, ref) => (
    <span {...props} ref={ref} style={{ display }}>
      {props.children}
    </span>
  )
);

export const Tooltip = ({ children, variant = 'light', display, disableWrapper, ...props }: TooltipProps) => (
  <MuiTooltip classes={{ tooltip: variant }} {...props}>
    {disableWrapper ? children : <TooltipContent display={display}>{children}</TooltipContent>}
  </MuiTooltip>
);
